import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, MessageCircle, Mail, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { templateService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast, Tab } from '../../components/ui';
import TemplateEditor from './TemplateEditor';

// Lead fields available as template variables
export const LEAD_FIELDS = [
  // Personal
  { key: 'fullName',        label: 'Full Name',           example: 'Rahul Sharma' },
  { key: 'email',           label: 'Email',               example: 'rahul@email.com' },
  { key: 'mobile',          label: 'Mobile',              example: '9876543210' },
  { key: 'alternateMobile', label: 'Alternate Mobile',    example: '9123456780' },
  { key: 'gender',          label: 'Gender',              example: 'male' },
  { key: 'age',             label: 'Age',                 example: '24' },
  { key: 'qualification',   label: 'Qualification',       example: 'B.Tech' },
  { key: 'occupation',      label: 'Occupation',          example: 'Software Engineer' },
  { key: 'company',         label: 'Company',             example: 'Infosys' },
  // Address
  { key: 'address',         label: 'Address',             example: '12 MG Road' },
  { key: 'city',            label: 'City',                example: 'Kochi' },
  { key: 'district',        label: 'District',            example: 'Ernakulam' },
  { key: 'state',           label: 'State',               example: 'Kerala' },
  { key: 'country',         label: 'Country',             example: 'India' },
  { key: 'pinCode',         label: 'Pin Code',            example: '682001' },
  // Course
  { key: 'course',          label: 'Course',              example: 'Data Science' },
  { key: 'preferredBatch',  label: 'Preferred Batch',     example: 'Morning' },
  { key: 'preferredLanguage', label: 'Preferred Language', example: 'English' },
  // Lead info
  { key: 'leadId',          label: 'Lead ID',             example: 'LD000042' },
  { key: 'counsellor',      label: 'Assigned Counsellor', example: 'Anita Nair' },
];

const TABS = [
  { key: 'all',      label: 'All' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email',    label: 'Email' },
];

const TYPE_META = {
  whatsapp: { color: '#25D366', icon: MessageCircle, label: 'WhatsApp' },
  email:    { color: '#3B82F6', icon: Mail,          label: 'Email' },
};

const TemplateManagement = () => {
  const { dark } = useTheme();
  const [tab, setTab] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // null = create new
  const [previewItem, setPreviewItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab !== 'all' ? { type: tab } : {};
      const { data } = await templateService.getAll(params);
      setTemplates(data.data || data || []);
    } catch {
      setToast({ msg: 'Failed to load templates', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    try {
      await templateService.remove(deleteTarget._id);
      setToast({ msg: 'Template deleted', type: 'success' });
      setDeleteTarget(null);
      load();
    } catch {
      setToast({ msg: 'Failed to delete template', type: 'error' });
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const card = {
    background: dark ? '#0F172A' : '#fff',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
    borderRadius: 16,
    overflow: 'hidden',
  };
  const rowStyle = {
    borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`,
    padding: '14px 20px',
  };
  const sub = dark ? '#64748B' : '#94A3B8';
  const text = dark ? '#F1F5F9' : '#0F172A';

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: text, margin: 0 }}>Message Templates</h1>
          <p style={{ fontSize: 13, color: sub, margin: '4px 0 0' }}>
            Create reusable WhatsApp and Email templates with dynamic lead fields.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
          <Plus size={15} style={{ marginRight: 6 }} /> New Template
        </Button>
      </div>

      {/* Available fields reference */}
      <div style={{
        background: dark ? '#0F172A' : '#F8FAFC',
        border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
        borderRadius: 12, padding: '12px 16px', marginBottom: 20,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: sub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Available Variables — insert as {'{{variableName}}'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {LEAD_FIELDS.map(f => (
            <span key={f.key} style={{
              fontFamily: 'monospace', fontSize: 12,
              background: dark ? '#1E293B' : '#EFF6FF',
              color: dark ? '#93C5FD' : '#2563EB',
              border: `1px solid ${dark ? '#334155' : '#BFDBFE'}`,
              borderRadius: 6, padding: '3px 8px',
            }}>
              {`{{${f.key}}}`}
              <span style={{ color: sub, fontFamily: 'sans-serif', marginLeft: 4 }}>= {f.label}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Template list */}
      <div style={card}>
        <div style={{ padding: '16px 20px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <Tab tabs={TABS} active={tab} onChange={(k) => { setTab(k); setExpandedId(null); }} />
        </div>

        {loading ? (
          <div style={{ padding: 20 }}><Skeleton rows={4} cols={4} /></div>
        ) : templates.length === 0 ? (
          <div style={{ padding: 40 }}>
            <EmptyState
              icon={Mail}
              title="No templates yet"
              description="Create your first template to send personalised messages to leads."
            />
          </div>
        ) : (
          templates.map(tmpl => {
            const meta = TYPE_META[tmpl.type];
            const Icon = meta.icon;
            const isExpanded = expandedId === tmpl._id;
            return (
              <div key={tmpl._id} style={rowStyle}>
                {/* Row header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    background: meta.color + '18',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={16} color={meta.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: text }}>{tmpl.name}</div>
                    {tmpl.subject && (
                      <div style={{ fontSize: 12, color: sub, marginTop: 2 }}>Subject: {tmpl.subject}</div>
                    )}
                  </div>
                  <Badge color={meta.color}>{meta.label}</Badge>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                    <button
                      title="Preview"
                      onClick={() => setExpandedId(isExpanded ? null : tmpl._id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: sub }}
                    >
                      {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                    <button
                      title="Edit"
                      onClick={() => { setEditing(tmpl); setEditorOpen(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: sub }}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      title="Delete"
                      onClick={() => setDeleteTarget(tmpl)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#EF4444' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Expanded body preview */}
                {isExpanded && (
                  <div style={{
                    marginTop: 12, padding: '12px 14px',
                    background: dark ? '#1E293B' : '#F8FAFC',
                    border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
                    borderRadius: 10,
                    fontFamily: tmpl.type === 'whatsapp' ? 'inherit' : 'inherit',
                    fontSize: 13, lineHeight: 1.7,
                    color: dark ? '#CBD5E1' : '#334155',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    maxHeight: 280, overflowY: 'auto',
                  }}>
                    {tmpl.body}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Editor modal */}
      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? 'Edit Template' : 'New Template'}
        width={680}
      >
        <TemplateEditor
          initial={editing}
          onSaved={() => { setEditorOpen(false); load(); setToast({ msg: editing ? 'Template updated' : 'Template created', type: 'success' }); }}
          onCancel={() => setEditorOpen(false)}
        />
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Template" width={400}>
        {deleteTarget && (
          <div>
            <p style={{ color: dark ? '#CBD5E1' : '#475569', margin: '0 0 20px' }}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                onClick={handleDelete}
                style={{ background: '#EF4444', color: '#fff', border: 'none' }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TemplateManagement;
